import { API_ROOT, http, SPATIAL_ROOT, type Fetch, type Http } from './http';

/**
 * The shop's standard cabinets, for the cabinets page's "Standard sizes" tab.
 *
 * <p>Read live from what the shop already publishes:
 * <ul>
 *   <li>the published carcass range (the same `spatial.range` the kitchen
 *       planner reads): its picker groups, and each group's units with their
 *       sizes and the fronts they need;</li>
 *   <li>each unit's price for this shopper from `POST /api/storefront/prices`,
 *       which applies the shopper's price band (retail or trade) on the server.</li>
 * </ul>
 * A unit the price list does not know is shown without a price and cannot be
 * added to the basket; nothing is ever invented to fill a gap.
 */
export interface CabinetGroup {
  /** `rangeKey:groupId`, unique across ranges. */
  id: string;
  name: string;
  section: string;
  parentName?: string;
  count: number;
}

export interface CabinetFront {
  width: number;
  height: number;
  quantity: number;
}

export type CabinetCategory = 'base' | 'wall' | 'tall' | 'wardrobe';

export interface CabinetProduct {
  /** The unit's definition id in the range. */
  id: string;
  sku: string;
  name: string;
  category: CabinetCategory;
  /** What its front is: a door, a stack of drawers, or open. */
  opening: 'door' | 'drawers' | 'none';
  w: number;
  h: number;
  d: number;
  legHeight: number;
  fronts: CabinetFront[];
  /** Shelves it comes with, counted from its parts. */
  shelves: number;
  /** A corner unit's arm depth (its back arm), where the parts give one. */
  armDepth?: number;
  /** A blind corner's blank section, from its id (`…-blank-left-400-800`). */
  blank?: { side: 'left' | 'right'; mm: number };
  /** Set only when the shop sells it: the commerce product and this shopper's unit price inc. VAT. */
  productId?: number;
  price?: number;
  available: boolean;
}

export interface CabinetCatalogue {
  groups(): Promise<CabinetGroup[]>;
  products(groupId: string): Promise<CabinetProduct[]>;
}

interface RangeIndex {
  ranges: Array<{
    key: string;
    name: string;
    projectRevision: string;
    groups: Array<{ id: string; name: string; section: string; parentName?: string; count: number }>;
  }>;
}

interface RangeDefinition {
  id: string;
  name: string;
  category: string;
  height: number;
  depth: number;
  widths: number[];
  legHeight?: number;
  installation?: { opening?: string };
  technical?: { requiredFronts?: Array<{ width: number; height: number; quantity: number }> };
  parts?: Array<{ id: string; size?: { x: number; y: number; z: number } }>;
  variants: Array<{ sku: string; width: number }>;
}

export interface PriceAnswer {
  currency: string;
  trade: boolean;
  prices: Array<{ sku: string; productId: number; unitPriceIncVat: string; available: boolean }>;
  unknown: string[];
}

/** "600 · High line base unit" → "High line base unit": the width is shown on its own. */
export function unitName(name: string): string {
  return name.replace(/^\d+\s*·\s*/, '');
}

const CATEGORIES: ReadonlySet<string> = new Set(['base', 'wall', 'tall', 'wardrobe']);
const OPENINGS: ReadonlySet<string> = new Set(['door', 'drawers', 'none']);

/** Prices for SKUs, in pages the endpoint accepts. */
export async function priceSkus(api: Http, skus: string[]): Promise<Map<string, PriceAnswer['prices'][number]>> {
  const out = new Map<string, PriceAnswer['prices'][number]>();
  for (let i = 0; i < skus.length; i += 500) {
    const answer = await api.send<PriceAnswer>('POST', `${API_ROOT}/prices`, { skus: skus.slice(i, i + 500) });
    for (const p of answer.prices) out.set(p.sku, p);
  }
  return out;
}

function armAndBlank(d: RangeDefinition): Pick<CabinetProduct, 'armDepth' | 'blank'> {
  const arm = d.parts?.find((part) => part.id === 'back-arm')?.size?.z;
  const blank = /-blank-(left|right)-(\d+)-/.exec(d.id);
  return {
    ...(arm ? { armDepth: arm } : {}),
    ...(blank ? { blank: { side: blank[1] as 'left' | 'right', mm: Number(blank[2]) } } : {}),
  };
}

export function toProducts(definitions: RangeDefinition[], prices: Map<string, PriceAnswer['prices'][number]>): CabinetProduct[] {
  const out: CabinetProduct[] = [];
  for (const d of definitions) {
    if (!CATEGORIES.has(d.category)) continue;
    for (const v of d.variants) {
      const price = prices.get(v.sku);
      const amount = price ? Number(price.unitPriceIncVat) : NaN;
      const sellable = Boolean(price?.available) && Number.isFinite(amount) && amount > 0;
      const opening = d.installation?.opening ?? 'door';
      out.push({
        id: d.id,
        sku: v.sku,
        name: d.name,
        category: d.category as CabinetCategory,
        opening: (OPENINGS.has(opening) ? opening : 'door') as CabinetProduct['opening'],
        w: v.width,
        h: d.height,
        d: d.depth,
        legHeight: d.legHeight ?? 0,
        fronts: (d.technical?.requiredFronts ?? []).map((f) => ({ width: f.width, height: f.height, quantity: f.quantity })),
        shelves: (d.parts ?? []).filter((part) => part.id.startsWith('shelf')).length,
        ...armAndBlank(d),
        ...(sellable ? { productId: price!.productId, price: Math.round(amount * 100) / 100 } : {}),
        available: sellable,
      });
    }
  }
  return out.sort((a, b) => a.w - b.w || a.name.localeCompare(b.name));
}

export function liveCabinetCatalogue(fetchImpl?: Fetch): CabinetCatalogue {
  const api = http(fetchImpl);
  let index: Promise<RangeIndex> | null = null;
  const loadIndex = () => {
    if (!index) {
      index = api.get<RangeIndex>(`${SPATIAL_ROOT}/kitchen/catalogue-ranges`);
      index.catch(() => {
        index = null;
      });
    }
    return index;
  };
  return {
    async groups() {
      const { ranges } = await loadIndex();
      return ranges.flatMap((r) =>
        r.groups.map((g) => ({ id: `${r.key}:${g.id}`, name: g.name, section: g.section, parentName: g.parentName, count: g.count })),
      );
    },
    async products(groupId) {
      const [key, group] = groupId.split(':');
      const range = (await loadIndex()).ranges.find((r) => r.key === key);
      if (!range || !group) return [];
      const { definitions } = await api.get<{ definitions: RangeDefinition[] }>(
        `${SPATIAL_ROOT}/kitchen/catalogue-ranges/${encodeURIComponent(range.key)}/revisions/${encodeURIComponent(range.projectRevision)}?group=${encodeURIComponent(group)}`,
      );
      const prices = await priceSkus(api, definitions.flatMap((d) => d.variants.map((v) => v.sku)));
      return toProducts(definitions, prices);
    },
  };
}
